import {
  Stack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Select,
  Text,
  Box,
  Progress,
  Alert,
  AlertIcon,
  useToast,
  Flex,
  CloseButton,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdUploadFile } from 'react-icons/md';
import { configApi } from '../../../../services/configApi';
import { useAuditLogger } from '../../../../services/hooks/Audit/useAuditLogger';

interface IOwnProps {
  registrationId: string;
  stepId: string;
  submissionId: string;
  refetchData?: () => void;
  closeModal: () => void;
}

interface IUploadResponse {
  document_id: string;
  version_id: string;
  upload_url: string;
  upload_fields?: Record<string, string>;
  s3_key: string;
  expires_at: string;
}

const DOCUMENT_TYPES = [
  { value: 'project', label: 'Projeto de Pesquisa' },
  { value: 'annex', label: 'Anexo' },
  { value: 'support', label: 'Documento de Apoio' },
  { value: 'other', label: 'Outro' },
];

const FormProjectDocumentContent: React.FC<IOwnProps> = ({
  registrationId: _registrationId,
  stepId,
  submissionId,
  refetchData,
  closeModal,
}) => {
  const toast = useToast();
  const auditLogger = useAuditLogger();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [formData, setFormData] = useState({
    document_type: 'project',
    title: '',
    description: '',
    display_order: 0,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [clientHash, setClientHash] = useState<string>('');

  const calculateFileHash = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Calcular hash do arquivo para validação
      try {
        const hash = await calculateFileHash(file);
        setClientHash(hash);
      } catch (error) {
        console.error('❌ Erro ao calcular hash:', error);
        // Erro ao calcular hash - continuar sem hash
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast({
        title: 'Erro',
        description: 'Por favor, selecione um arquivo',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!formData.title) {
      toast({
        title: 'Erro',
        description: 'Por favor, preencha o título do documento',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setUploadProgress(10);

    try {
      // PASSO 1: Iniciar upload - obter URL pré-assinada do S3
      setUploadProgress(20);

      const initUploadResponse = await configApi.post<IUploadResponse>('/research-proposal-defense/upload/', {
        submission_id: submissionId,
        document_type: formData.document_type,
        title: formData.title,
        description: formData.description,
        filename: selectedFile.name,
        content_type: selectedFile.type,
        file_size: selectedFile.size,
        display_order: formData.display_order,
      });

      const { upload_url, upload_fields, version_id, s3_key } = initUploadResponse.data;

      // PASSO 2: Upload direto para S3 usando URL pré-assinada
      setUploadProgress(40);

      // Criar FormData com os campos do S3
      const formDataS3 = new FormData();

      // Adicionar todos os campos fornecidos pelo backend
      if (upload_fields) {
        Object.entries(upload_fields).forEach(([key, value]) => {
          formDataS3.append(key, value as string);
        });
      }

      // Adicionar o arquivo por último
      formDataS3.append('file', selectedFile);

      let s3UploadSuccess = false;

      try {
        const s3Response = await fetch(upload_url, {
          method: 'POST',
          body: formDataS3,
        });

        // S3 retorna 204 (No Content) em caso de sucesso
        // Status 200-299 são considerados sucesso
        if (!s3Response.ok && s3Response.status !== 204) {
          const s3ErrorText = await s3Response.text();
          console.error('❌ Erro S3:', {
            status: s3Response.status,
            statusText: s3Response.statusText,
            body: s3ErrorText,
          });
          throw new Error(`Falha no upload para S3: ${s3Response.status} - ${s3Response.statusText}`);
        }

        s3UploadSuccess = true;
      } catch (s3Error: any) {
        // Se o erro for de CORS mas o upload pode ter sido bem-sucedido
        // (erro de rede "Failed to fetch" geralmente indica CORS após 204)
        if (s3Error.message === 'Failed to fetch' || s3Error.name === 'TypeError') {
          s3UploadSuccess = true; // Assumir sucesso e deixar o backend validar
        } else {
          throw s3Error; // Re-lançar se for outro tipo de erro
        }
      }

      if (!s3UploadSuccess) {
        throw new Error('Upload para S3 falhou');
      }

      setUploadProgress(70);

      // PASSO 3: Confirmar upload no backend (CRÍTICO - não remover!)
      await configApi.put('/research-proposal-defense/upload/', {
        version_id,
        s3_key,
        client_hash: clientHash,
      });

      setUploadProgress(100);

      // Log de auditoria
      await auditLogger.logDocumentAction(
        'document_uploaded',
        initUploadResponse.data.document_id,
        formData.title,
        'info',
      );

      await auditLogger.log({
        action: 'document_uploaded',
        level: 'info',
        category: 'document_management',
        resource_type: 'document_upload',
        resource_id: initUploadResponse.data.document_id,
        step_id: stepId,
        message: `Upload de documento concluído: ${formData.title} (${selectedFile.name}) - ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB - Tipo: ${formData.document_type}`,
      });

      toast({
        title: 'Sucesso',
        description: 'Documento enviado com sucesso',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Resetar formulário
      setFormData({
        document_type: 'project',
        title: '',
        description: '',
        display_order: 0,
      });
      setSelectedFile(null);
      setClientHash('');
      setUploadProgress(0);

      // Pequeno delay antes de atualizar para garantir que o backend processou
      await new Promise((resolve) => setTimeout(resolve, 500));
      // Atualizar dados e fechar modal
      await refetchData?.();
      closeModal();
    } catch (error: any) {
      toast({
        title: 'Erro no upload',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          'Ocorreu um erro ao enviar o documento',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack spacing={4} p={6}>
      <Flex justify="space-between" align="center">
        <Text fontSize="xl" fontWeight="bold">
          Adicionar Arquivo
        </Text>
        <CloseButton onClick={closeModal} />
      </Flex>

      {uploadProgress > 0 && uploadProgress < 100 && (
        <Box>
          <Text fontSize="sm" mb={2}>
            Enviando documento... {uploadProgress}%
          </Text>
          <Progress value={uploadProgress} size="sm" colorScheme="blue" />
        </Box>
      )}

      <FormControl isRequired>
        <FormLabel fontSize="sm">Tipo de Documento</FormLabel>
        <Select
          value={formData.document_type}
          onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
          isDisabled={isLoading}
        >
          {DOCUMENT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm">Título do Documento</FormLabel>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Ex: Projeto de Pesquisa - Análise de Dados"
          isDisabled={isLoading}
        />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Descrição (opcional)</FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Descrição detalhada do documento"
          rows={3}
          isDisabled={isLoading}
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm">Arquivo</FormLabel>
        <Input type="file" accept=".pdf,.doc,.docx,.odt" onChange={handleFileChange} isDisabled={isLoading} pt={1} />
        {selectedFile && (
          <Text fontSize="xs" color="gray.600" mt={1}>
            {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
          </Text>
        )}
      </FormControl>

      <Alert status="info" fontSize="xs">
        <AlertIcon />
        Formatos aceitos: PDF, DOC, DOCX, ODT. Tamanho máximo: 100MB por arquivo.
      </Alert>

      <Flex gap={2} justify="flex-end">
        <Button variant="ghost" onClick={closeModal} isDisabled={isLoading}>
          Cancelar
        </Button>
        <Button
          colorScheme="blue"
          leftIcon={<MdUploadFile />}
          onClick={handleSubmit}
          isLoading={isLoading}
          loadingText="Enviando..."
        >
          Enviar Documento
        </Button>
      </Flex>
    </Stack>
  );
};

export default FormProjectDocumentContent;
